import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { CircleQuestionMark } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Tooltip } from "components/tooltip/Tooltip";
import SearchGroupInput from "../input/SearchGroupInput.component";
import ListWithActionsComponent from "../listWithActions/ListWithActions.component";
import {SegmentedControl} from "components/common/SegmentedControl.component";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {useTranslation} from "components/translations/useTranslation";
import * as Api from "../../utils/api/api";
import {USE_MOCK_MANAGERS, getMockNearestManagers} from "./managers.mockData";
import {useSelector} from "react-redux";
import * as reduxSelectors from "../store/application.reducers";
import {userHasAccessRights} from "utils/accessRights.function.js";
import {handleError, insufficientRightsError} from "utils/errorHandle.function.js";
import {formatNameWithEmail} from "utils/userFormatter.function";
import {canViewConfiguration} from "../menu/Menu.component";
import {
    HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID,
} from "components/tooltip/AppTooltip.component";
import {
    EMPLOYEE_BY_MANAGER,
    MANAGER_BY_EMPLOYEE,
    NearestManagerMode,
    NearestManagerRow,
    NearestManagersResponse
} from "types/managers";

const FIRST_PAGE = 1;
const ITEMS_PER_PAGE = 20;
// the endpoint takes a sort direction, but the page offers no way to flip it
const SORT_ASCENDING = true;

// the list is an untyped React.memo, which TypeScript reads as taking no props at all
const ListWithActions: any = ListWithActionsComponent;

function computeHeader(isManagerByEmployee: boolean) {
    return [
        {
            value: "left",
            text: isManagerByEmployee ? "managers.employee" : "managers.manager",
            class: "col-md-6"
        },
        {
            value: "right",
            text: isManagerByEmployee ? "managers.manager" : "managers.employee",
            class: "col-md-6"
        }
    ];
}

export const Managers = () => {

    const {translate} = useTranslation();
    const userData = useSelector(reduxSelectors.getUsersData);
    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    // the list memoises its rows, and needs the language to know when a translation went stale
    const currentLanguage = useSelector(reduxSelectors.getLanguage);

    const [searchValue, setSearchValue] = useState('');
    const [nearestManagerMode, setNearestManagerMode] = useState<NearestManagerMode>(MANAGER_BY_EMPLOYEE);
    const [page, setPage] = useState(FIRST_PAGE);
    const [rows, setRows] = useState<NearestManagerRow[]>([]);
    const [records, setRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const stopScroll = useRef(false);

    // every call gets a ticket, and only the newest one is allowed to write to the state
    const latestRequest = useRef(0);

    /**
     * The query is passed in rather than read off the state, so that a handler can load the page it
     * is switching to without having to wait for its own state update to land.
     */
    const fetchManagers = useCallback((requestedPage: number, mode: NearestManagerMode, search: string) => {
        const requestId = ++latestRequest.current;
        setIsLoading(true);

        const request = USE_MOCK_MANAGERS
            ? getMockNearestManagers(requestedPage, ITEMS_PER_PAGE, mode, SORT_ASCENDING, search)
            : Api.getNearestManagers(requestedPage, ITEMS_PER_PAGE, mode, SORT_ASCENDING, search, currentContextId);

        request.then((response: NearestManagersResponse) => {
                if (requestId !== latestRequest.current) {
                    // a newer request has already been fired, so this response is stale
                    return;
                }
                setPage(response.page);
                setRecords(response.records);
                // the first page replaces what is on screen, every page after it extends the list
                setRows(previous => requestedPage === FIRST_PAGE ? response.rows : [...previous, ...response.rows]);
                setIsLoading(false);
            },
            (error: any) => {
                if (requestId !== latestRequest.current) {
                    return;
                }
                setIsLoading(false);
                console.error(error);
            });
    }, [currentContextId]);

    useEffect(() => {
        // with no location to go on, the rights check reads the path the browser is already on
        const userRoles = userData?.userRoles;
        if (!userRoles) {
            return;
        }
        if (!userHasAccessRights(userRoles) || !canViewConfiguration(userRoles)) {
            handleError(insufficientRightsError);
        }
    }, [userData]);
    // loads on mount, and starts over whenever the company context changes
    useEffect(() => {
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, searchValue);
    }, [currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- the mode and the search term reload through their own handlers

    // re-arms the infinite scroll after every render, so the next bottom hit is served
    useEffect(() => {
        stopScroll.current = false;
    });

    const inputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, value);
    }, [fetchManagers, nearestManagerMode]);

    const clearSearch = useCallback(() => {
        setSearchValue('');
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, '');
    }, [fetchManagers, nearestManagerMode]);

    const onModeChange = useCallback((mode: NearestManagerMode) => {
        if (mode === nearestManagerMode) {
            return;
        }
        setNearestManagerMode(mode);
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, mode, searchValue);
    }, [fetchManagers, nearestManagerMode, searchValue]);

    const loadMoreItems = useCallback(() => {
        if (stopScroll.current) {
            return;
        }
        stopScroll.current = true;
        if (rows.length > 0 && rows.length < records) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchManagers(nextPage, nearestManagerMode, searchValue);
        }
    }, [fetchManagers, nearestManagerMode, page, records, rows.length, searchValue]);

    const isManagerByEmployee = nearestManagerMode === MANAGER_BY_EMPLOYEE;

    const header = useMemo(() => computeHeader(isManagerByEmployee), [isManagerByEmployee]);

    const list = useMemo(() => rows.map(row => {
        const employee = formatNameWithEmail(`${row.employeeFirstName} ${row.employeeLastName} (${row.employeeEmail})`);
        const manager = formatNameWithEmail(`${row.managerFirstName} ${row.managerLastName} (${row.managerEmail})`);

        return isManagerByEmployee
            ? {left: employee, right: manager}
            : {left: manager, right: employee};
    }), [rows, isManagerByEmployee]);

    return (
        // the outer wrapper is what ConfigurationWrapper used to render around every configuration page
        <div>
            <div className="w-100">
                <main className="page w-100 overflow-y-auto d-flex justify-content-center align-items-start">
                    <div className="py-16 mb-48 w-75 settings-page__container">
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("managers.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID}
                                  data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="managers-header-info-tooltip">
                                <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                            </span>
                            <Tooltip
                                id="managers-header-info-tooltip"
                                content={() => translate("managers.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main">
                            <div className="d-flex justify-content-end gap-3 align-items-center mb-24">
                                <SearchGroupInput placeholder={translate("managers.search")}
                                                  value={searchValue}
                                                  className="ga-input__default"
                                                  onInputChange={inputChange}
                                                  onClearInput={clearSearch}
                                                  translate={translate}/>

                                <SegmentedControl
                                    options={[
                                        {
                                            key: MANAGER_BY_EMPLOYEE,
                                            label: translate("managers.managerByEmployee"),
                                            isSelected: isManagerByEmployee,
                                            onClick: () => onModeChange(MANAGER_BY_EMPLOYEE)
                                        },
                                        {
                                            key: EMPLOYEE_BY_MANAGER,
                                            label: translate("managers.employeesByManager"),
                                            isSelected: !isManagerByEmployee,
                                            onClick: () => onModeChange(EMPLOYEE_BY_MANAGER)
                                        }
                                    ]}
                                />
                            </div>

                            {(!isLoading || list.length > 0) &&
                                <ListWithActions header={header}
                                                 values={list}
                                                 showActions={false}
                                                 scrollAtBottom={loadMoreItems}
                                                 emptyMessage="managers.noMatches"
                                                 language={currentLanguage}
                                                 translate={translate}/>
                            }

                            {isLoading && list.length === 0 &&
                                <Loader size={LoaderSize.Medium}/>
                            }
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Managers;
